import { Card, CardContent, Typography, useTheme } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import * as echarts from 'echarts';
import { getLastSevenDates } from "../utils/tool";
import { useDataProvider } from "react-admin";

type EChartsOption = echarts.EChartsOption;

// 更健壮的日期格式化函数
const formatDateToMMDD = (dateStr: string) => {
    try {
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr; // 无效日期处理
        
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');
        return `${month}-${day}`;
    } catch (e) {
        return dateStr;
    }
};

// 获取最近7天的日期
const lastSevenDates = getLastSevenDates();

export const DeviceChart = () => {
    const chartRef = useRef<HTMLDivElement>(null);
    const chartInstanceRef = useRef<echarts.ECharts | null>(null);
    const theme = useTheme();
    const dataProvider = useDataProvider();
    const [chartData, setChartData] = useState<any[]>([]);
    const [customerNames, setCustomerNames] = useState<string[]>([]); // 初始化为空数组
    const [isLoading, setIsLoading] = useState(true);

    // 获取接口数据
    useEffect(() => {
        let isMounted = true; // 防止组件卸载后设置状态
        
        const fetchActivationData = async () => {
            try {
                setIsLoading(true);
                const response = await dataProvider.getLast7daysActivation();
                
                if (!isMounted) return;
                
                // 确保正确处理响应数据
                const data = response && response.data ? response.data : [];
                setChartData(data);

                // 提取所有唯一的销售客户名称
                const names = new Set<string>();
                (data || []).forEach((day: any) => {
                    day.salesCustomerList?.forEach((customer: any) => {
                        if (customer && customer.salesCustomerName) {
                            names.add(customer.salesCustomerName);
                        }
                    });
                });
                setCustomerNames(Array.from(names));
            } catch (error) {
                console.error("获取近7天设备激活数量失败:", error);
                if (isMounted) {
                    setChartData([]);
                    setCustomerNames([]);
                }
            } finally {
                if (isMounted) setIsLoading(false);
            }
        };

        fetchActivationData();
        
        return () => {
            isMounted = false;
        };
    }, [dataProvider]);

    // 处理图表渲染
    useEffect(() => {
        if (!chartRef.current) return;
        
        // 初始化图表实例（如果不存在）
        if (!chartInstanceRef.current) {
            chartInstanceRef.current = echarts.init(chartRef.current);
        }
        
        const chartInstance = chartInstanceRef.current;
        
        // 如果没有数据或客户名称为空，显示空图表
        if (isLoading) {
            chartInstance.setOption({
                title: {
                    text: '加载中...',
                    left: 'center',
                    top: 'center',
                    textStyle: {
                        color: theme.palette.text.secondary,
                        fontSize: 16
                    }
                }
            }, true); // 使用 true 来清除之前的选项
            return;
        }
        
        if (chartData.length === 0 || customerNames.length === 0) {
            chartInstance.setOption({
                title: {
                    text: '暂无数据',
                    left: 'center',
                    top: 'center',
                    textStyle: {
                        color: theme.palette.text.secondary,
                        fontSize: 16
                    }
                }
            }, true); // 使用 true 来清除之前的选项
            return;
        }

        // 1. 创建日期映射，方便快速查找
        const dateMap: Record<string, Record<string, number>> = {};
        chartData.forEach((day) => {
            const formattedDate = formatDateToMMDD(day.dataDate);
            dateMap[formattedDate] = {};

            day.salesCustomerList?.forEach((customer: any) => {
                dateMap[formattedDate][customer.salesCustomerName] = customer.deviceActiveCount;
            });
        });

        // 2. 准备系列数据
        const seriesData = customerNames.map(name => ({
            name,
            type: 'bar',
            barGap: 0,
            emphasis: { focus: 'series' },
            data: lastSevenDates.map(date => dateMap[date]?.[name] || 0)
        }));

        // 3. 配置图表选项
        const option: EChartsOption = {
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' },
                formatter: (params: any) => {
                    const date = params[0].axisValue;
                    const total = params.reduce((sum: number, item: any) => sum + (item.value || 0), 0);
                    return `${date}<br/>${params.map((item: any) => {
                        const value = item.value || 0;
                        const percentage = total === 0 ? 0 : (value / total * 100);
                        return `${item.marker} ${item.seriesName}: ${value} (${percentage.toFixed(1)}%)`;
                    }).join('<br/>')}`;
                }
            },
            legend: {
                data: customerNames,
                textStyle: {
                    color: theme.palette.text.secondary,
                    fontSize: 16
                }
            },
            xAxis: {
                type: 'category',
                data: lastSevenDates,
                axisTick: { show: false },
                axisLabel: {
                    color: theme.palette.text.secondary
                }
            },
            yAxis: {
                type: 'value',
                axisLabel: {
                    color: theme.palette.text.secondary
                }
            },
            grid: {
                bottom: 0,
                left: 10,
                right: 10,
                containLabel: true
            },
            series: seriesData,
        };

        chartInstance.setOption(option, true); // 使用 true 来清除之前的选项

        // 响应式处理
        const resizeHandler = () => chartInstance.resize();
        window.addEventListener('resize', resizeHandler);
        
        return () => {
            window.removeEventListener('resize', resizeHandler);
        };
    }, [theme, chartData, customerNames, isLoading]);

    // 组件卸载时销毁图表实例
    useEffect(() => {
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.dispose();
                chartInstanceRef.current = null;
            }
        };
    }, []);

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent sx={{ 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                position: 'relative' 
            }}>
                <div style={{ color: '#1c3166', fontSize: '26px', fontWeight: 'bold' }}>
                    近7天激活设备
                </div>
                
                {isLoading && (
                    <div style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.7)',
                        zIndex: 10
                    }}>
                        <Typography variant="h6">加载中...</Typography>
                    </div>
                )}
                
                <div 
                    ref={chartRef} 
                    style={{ 
                        width: '100%', 
                        height: '100%', 
                        minHeight: '300px',
                        opacity: isLoading ? 0.5 : 1 
                    }} 
                />
            </CardContent>
        </Card>
    );
};